import React, { useState, useCallback } from 'react';
import { CompositionComponentUpdate, CompositionProcessUpdate } from '../types/costwand';
import { Button } from './ui/button.tsx';
import { Input } from './ui/input.tsx';
import { Label } from './ui/label.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select.tsx'; // Import Select components
import { ProductLookupItem, ProcessLookupItem } from '../types/costwand'; // Import lookup types
import { Spinner } from './ui/spinner.tsx'; // Import Spinner

interface CompositionEditComponentProps {
  componentIndex: number;
  component: CompositionComponentUpdate;
  onComponentChange: (index: number, field: string, value: any) => void; // Allow any type for value
  onRemoveComponent: (index: number) => void;
  onProcessChange: (componentIndex: number, processIndex: number, field: string, value: any) => void; // Allow any type for value
  onRemoveProcess: (componentIndex: number, processIndex: number) => void;
  onAddProcess: (componentIndex: number, process_id: number, process_labels: any) => void; // Modified to accept process_id and labels
  products: ProductLookupItem[]; // Added for component selection
  processes: ProcessLookupItem[]; // Added for process selection
  lookupLoading: boolean; // Added to disable inputs during loading
}

const CompositionEditComponent: React.FC<CompositionEditComponentProps> = ({
  componentIndex,
  component,
  onComponentChange,
  onRemoveComponent,
  onProcessChange,
  onRemoveProcess,
  onAddProcess,
  products,
  processes,
  lookupLoading,
}) => {
  // NEW: State for the "Add New Process" dropdown within this component
  const [selectedAddProcessId, setSelectedAddProcessId] = useState<string>('');

  const handleInputChange = useCallback((field: string, value: any) => {
    onComponentChange(componentIndex, field, value);
  }, [componentIndex, onComponentChange]);

  const handleProcessInputChange = useCallback((processIndex: number, field: string, value: any) => {
    onProcessChange(componentIndex, processIndex, field, value);
  }, [componentIndex, onProcessChange]);

  const handleAddProcessClick = (process_id: number, process_labels: any) => {
    onAddProcess(componentIndex, process_id, process_labels);
  };

  return (
    <div className="mb-4 p-4 border rounded-md bg-white shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h4 className="font-semibold text-lg">
          Component: {component._labels?.component_id || `ID: ${component.component_id}`}
          {component._labels?.unit_of_measure_id && ` [${component._labels.unit_of_measure_id}]`} {/* ADDED LINE */}
        </h4>
        <Button variant="destructive" size="sm" onClick={() => onRemoveComponent(componentIndex)}>
          Delete Component
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor={`component_id_${componentIndex}`}>Component</Label>
          <Input
            type="text"
            id={`component_id_${componentIndex}`}
            value={component._labels?.component_id || String(component.component_id)}
            disabled
            className="bg-muted text-muted-foreground"
          />
        </div>
        <div>
          <Label htmlFor={`overhead_charge_${componentIndex}`}>Overhead Charge (%)</Label>
          <Input
            type="number"
            id={`overhead_charge_${componentIndex}`}
            value={String(component.overhead_charge ?? '')}
            onChange={(e) => handleInputChange('overhead_charge', parseFloat(e.target.value))}
            disabled={lookupLoading}
          />
        </div>
        <div>
          <Label htmlFor={`required_qty_${componentIndex}`}>Required Quantity</Label>
          <Input
            type="number"
            id={`required_qty_${componentIndex}`}
            value={String(component.required_qty ?? '')}
            onChange={(e) => handleInputChange('required_qty', parseFloat(e.target.value))}
            disabled={lookupLoading}
          />
        </div>
      </div>

      <h5 className="font-semibold mt-6 mb-2">Processes:</h5>
      {component.processes && component.processes.map((process, processIndex) => (
        <div key={processIndex} className="mb-3 p-3 border rounded-md bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label htmlFor={`process_id_${componentIndex}_${processIndex}`}>Process</Label>
              <Select
                onValueChange={(value) => {
                  const selectedProcess = processes.find(p => String(p.id) === value);
                  if (selectedProcess) {
                    handleProcessInputChange(processIndex, 'process_id', selectedProcess.id);
                    handleProcessInputChange(processIndex, '_labels', { process_id: selectedProcess.description });
                  }
                }}
                value={String(process.process_id)}
                disabled={lookupLoading}
              >
                <SelectTrigger id={`process_id_${componentIndex}_${processIndex}`}>
                  <SelectValue placeholder={lookupLoading ? <span className="flex items-center gap-2"><Spinner size="sm" /> Loading processes...</span> : "Select a process"}>
                    {process._labels?.process_id || `ID: ${process.process_id}`}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {processes.map((p) => (
                    <SelectItem key={p.id} value={String(p.id)}>
                      {p.description}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor={`cost_${componentIndex}_${processIndex}`}>Cost</Label>
              <Input
                type="number"
                id={`cost_${componentIndex}_${processIndex}`}
                value={String(process.cost ?? '')}
                onChange={(e) => handleProcessInputChange(processIndex, 'cost', parseFloat(e.target.value))}
                disabled={lookupLoading}
              />
            </div>
            <div>
              <Label htmlFor={`required_qty_${componentIndex}_${processIndex}`}>Required Quantity</Label>
              <Input
                type="number"
                id={`required_qty_${componentIndex}_${processIndex}`}
                value={String(process.required_qty ?? '')}
                onChange={(e) => handleProcessInputChange(processIndex, 'required_qty', parseFloat(e.target.value))}
                disabled={lookupLoading}
              />
            </div>
          </div>
          <Button variant="destructive" size="sm" onClick={() => onRemoveProcess(componentIndex, processIndex)} className="mt-2">
            Delete Process
          </Button>
        </div>
      ))}
      <div className="mt-2">
        <Label htmlFor={`add_process_select_${componentIndex}`} className="block text-sm font-medium text-foreground mb-1">Add New Process</Label>
        <Select
          onValueChange={(value) => {
            setSelectedAddProcessId(value); // Update local state
            const selectedProcess = processes.find(p => String(p.id) === value);
            if (selectedProcess) {
              handleAddProcessClick(selectedProcess.id, { process_id: selectedProcess.description });
              setSelectedAddProcessId(''); // Reset after adding
            }
          }}
          value={selectedAddProcessId} // Control the value
          disabled={lookupLoading}
        >
          <SelectTrigger id={`add_process_select_${componentIndex}`}>
            <SelectValue placeholder={lookupLoading ? <span className="flex items-center gap-2"><Spinner size="sm" /> Loading processes...</span> : "Select a process to add"} />
          </SelectTrigger>
          <SelectContent>
            {processes.map((p) => (
              <SelectItem key={p.id} value={String(p.id)}>
                {p.description}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

export default CompositionEditComponent;